import { FreshnessIndicator } from '../../../shared/dashboard/FreshnessIndicator';
import { formatPeriodRange, isValidTimeZone } from '../../../shared/dashboard/dashboardFormat';
import type { DashboardPeriod } from '../../dashboard-v2/types';
import { CompanyTimeZoneControl } from './CompanyTimeZoneControl';

/**
 * The first thing on the page: whose figures these are, in what role they are being viewed, on which plan, for which period and compared with what,
 * in which time zone and currency, and how fresh they are. Every value comes from the signed-in session or the server's own response; nothing here
 * is chosen by the browser. (The staging banner above the page already states when the data is synthetic.)
 */
export function ExecutiveDashboardHeader({
  companyName, roleName, planName, period, generatedAtUtc, savedTimeZoneId, onTimeZoneSaved,
}: {
  companyName: string | null;
  roleName: string | null;
  planName: string | null;
  period: DashboardPeriod;
  generatedAtUtc: string;
  /** The company's saved zone (from the session), or null when none is chosen yet. */
  savedTimeZoneId: string | null;
  onTimeZoneSaved: () => void;
}) {
  const isSet = isValidTimeZone(savedTimeZoneId);
  const effectiveZone = isSet ? savedTimeZoneId : 'UTC';
  const context = [companyName, roleName ? `Viewing as ${roleName}` : null, planName ? `${planName} plan` : 'No plan'].filter(Boolean).join(' · ');

  return (
    <header className="ed-exec-header" data-testid="executive-dashboard-header">
      <div>
        <h2 className="ed-exec-header__title">Payroll and workforce cost</h2>
        <p className="ed-exec-header__context">{context}</p>
      </div>
      <dl className="ed-exec-header__meta">
        <div>
          <dt>{period.label}</dt>
          <dd>{formatPeriodRange(period.start, period.end)}</dd>
        </div>
        <div>
          <dt>Compared with {period.comparisonLabel.toLowerCase()}</dt>
          <dd>{formatPeriodRange(period.comparisonStart, period.comparisonEnd)}</dd>
        </div>
        <div>
          <dt>Time zone</dt>
          <dd><CompanyTimeZoneControl effectiveZoneId={effectiveZone} isSet={isSet} onSaved={onTimeZoneSaved} /></dd>
        </div>
        <div>
          <dt>Currency</dt>
          <dd>US dollars (USD)</dd>
        </div>
        <div className="ed-exec-header__freshness">
          <FreshnessIndicator generatedAtUtc={generatedAtUtc} timeZoneId={effectiveZone} />
        </div>
      </dl>
    </header>
  );
}
